import React, { useState, useEffect, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { fetchzcql } from './../api/reusableApis';

const RevenueInsights = () => {
    const [teamFilter, setTeamFilter] = useState('this month');
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);

    const getDates = (filter) => {
        const now = new Date();
        let start = new Date();
        start.setHours(0, 0, 0, 0);

        if (filter === 'today') {
            return { start: now.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
        } else if (filter === 'yesterday') {
            start.setDate(now.getDate() - 1);
            let end = new Date();
            end.setDate(now.getDate() - 1);
            return { start: start.toISOString().split('T')[0], end: end.toISOString().split('T')[0] };
        } else if (filter === 'this week') {
            start.setDate(now.getDate() - 7);
        } else if (filter === 'this month') {
            start.setMonth(now.getMonth(), 1);
        }

        return {
            start: start.toISOString().split('T')[0],
            end: now.toISOString().split('T')[0]
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const { start, end } = getDates(teamFilter);
                const query = `select total_amount, booking_date from bookings where booking_date Between '${start}' and '${end}' and (status = 'BOOKED' or status = 'COMPLETED')`;

                const res = await fetchzcql(query);
                const rawData = res?.data?.response || [];

                if (rawData.length === 0) {
                    setChartData([]);
                    return;
                }

                const grouped = rawData.reduce((acc, item) => {
                    const { total_amount, booking_date } = item.bookings;
                    const cleanAmount = typeof total_amount === 'string'
                        ? parseFloat(total_amount.replace(/[^0-9.]/g, ''))
                        : parseFloat(total_amount);

                    acc[booking_date] = (acc[booking_date] || 0) + (cleanAmount || 0);
                    return acc;
                }, {});

                const formattedData = Object.keys(grouped)
                    .sort((a, b) => new Date(a) - new Date(b))
                    .map(date => ({
                        name: new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
                        revenue: grouped[date]
                    }));

                setChartData(formattedData);
            } catch (error) {
                console.error("Database Error:", error);
                setChartData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [teamFilter]);

    const totalRevenue = useMemo(() =>
        chartData.reduce((sum, item) => sum + item.revenue, 0), [chartData]
    );

    return (
        <div className="bg-[#FAFAFB] font-sans  ">
            <div className="max-w-6xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-10">
                    <div>
                        <h1 className="text-3xl font-black text-slate-900 tracking-tight">Revenue</h1>
                        <p className="text-slate-400 text-sm font-medium mt-0.5">
                            {loading ? "Syncing latest data..." : "Live Performance (INR)"}
                        </p>
                    </div>

                    <div className="flex bg-white border border-slate-200 rounded-2xl p-1 shadow-sm w-full sm:w-auto overflow-x-auto">
                        {["today", "yesterday", "this week", "this month"].map((status) => (
                            <button
                                key={status}
                                onClick={() => setTeamFilter(status)}
                                className={`px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-tight transition-all flex-1 sm:flex-none whitespace-nowrap ${teamFilter === status
                                    ? "bg-purple-600 text-white shadow-md shadow-purple-200"
                                    : "text-slate-400 hover:text-slate-600"
                                    }`}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="bg-white rounded-[24px] p-6 sm:p-8 shadow-sm border border-slate-100">
                    <div className="flex justify-between items-end mb-8">
                        <div>
                            <span className="text-[11px] font-bold text-purple-600 uppercase tracking-widest">Growth Analytics</span>
                            <h3 className="text-xl font-bold text-slate-800 capitalize mt-1">{teamFilter} Trends</h3>
                        </div>
                        <div className="text-right">
                            <p className="text-2xl sm:text-3xl font-black text-slate-900 transition-all">
                                ₹{totalRevenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </p>
                            <p className="text-xs font-bold text-emerald-500">Net Revenue</p>
                        </div>
                    </div>

                    {/* Chart / Loading / Empty State Container */}
                    <div className="h-80 w-full flex items-center justify-center">
                        {loading ? (
                            <div className="flex flex-col items-center gap-2">
                                <div className="w-8 h-8 border-4 border-purple-100 border-t-purple-600 rounded-full animate-spin"></div>
                                <p className="text-slate-400 text-xs font-bold uppercase tracking-tighter">Fetching Data</p>
                            </div>
                        ) : chartData.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 20, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#9333ea" stopOpacity={1} />
                                            <stop offset="100%" stopColor="#c084fc" stopOpacity={0.8} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                    <XAxis
                                        dataKey="name"
                                        axisLine={false}
                                        tickLine={false}
                                        tick={{ fill: '#94A3B8', fontSize: 10, fontWeight: 700 }}
                                        dy={10}
                                        interval={teamFilter === 'this month' ? 4 : 0}
                                    />
                                    <YAxis
                                        axisLine={false}
                                        tickLine={false}
                                        width={75}
                                        tick={{ fill: '#94A3B8', fontSize: 11, fontWeight: 600 }}
                                        tickFormatter={(val) => `₹${val >= 1000 ? (val / 1000).toFixed(1) + 'k' : val}`}
                                    />
                                    <Tooltip
                                        cursor={{ fill: '#F8FAFC', radius: 8 }}
                                        formatter={(value) => [`₹${value.toLocaleString('en-IN')}`, 'Revenue']}
                                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                                    />
                                    <Bar
                                        dataKey="revenue"
                                        fill="url(#barGradient)"
                                        radius={[6, 6, 6, 6]}
                                        barSize={teamFilter === 'this month' ? 12 : 35}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex flex-col items-center text-center animate-in fade-in zoom-in duration-300">
                                <div className="bg-slate-50 rounded-2xl p-4 mb-4">
                                    <svg className="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <h4 className="text-slate-900 font-bold">No Revenue Data</h4>
                                <p className="text-slate-400 text-sm max-w-[200px] mt-1">
                                    We couldn't find any bookings for the selected period.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RevenueInsights;